<script setup lang="ts">
import ErrorBoundary from "@/components/ErrorBoundary/index.vue";
import OpenningPage from "@/components/OpenningPage/index.vue";
import getRoutes, { genRoutes } from "@/router/initRoutes";
import {
  useSystemInfo,
  useActivityList,
  useGameCategory,
  useHomeBannersAndTexts,
  useOpenningImage,
  useWallet,
  useServico,
  useAppPwa,
} from "@/store";
import useAPP from "@/hooks/useInstall";
import { ev, CUSTOM_PROTOCOL, INSTALLCONFIG_TIME } from "@/consts";
import { wait } from "@/utils";
import {
  sealing,
  isTemplate,
  isPlatform,
  isBuild,
  isIOSSafari,
  isInPWA,
  hasNetwork,
} from "@/env";
import { usePWAJumpIframe } from "@/hooks/usePWAJumpIframe";
import { callJSBridge } from "@/utils";
import createHeadLinks from "@/utils/createHeadLinks";
import { needUpdate } from "@/utils/updateChecker";
import { checker } from "@/utils/updateChecker";
import showPopup from "@a/components/showPopup";
import Reload from "@/components/common/Reload.vue";
import notificationModal from "@/themes/theme_a/views/Home/notificationModal.vue";
import { useUserInfo } from "@/store";
import FullScreenLoading from "@/components/FullScreenLoading/index.vue";
import { isInIframe } from "@/env";
import i18n from '@/i18n'
const { iframeUrl } = usePWAJumpIframe();

const isLoading = ref(true);
const router = useRouter();
const route = useRoute();
// const userInfo = useUserInfo();
if (isBuild && !localStorage._checker) {
  console.log("start checker----------start");
  checker.start();
}

const genCommonRoutes = async () => {
  const themeModule = await import('@/router/theme_common').then(m => m.default)
  const theme = useSystemInfo().theme
  const routes = await genRoutes(themeModule, theme)
  routes.forEach(router.addRoute);
}

const init = async () => {
  if (route.query.s === "1") return;

  await Promise.all([useAPP().init(),useHomeBannersAndTexts().init()]);
        
  if (isTemplate) {
    //模版页面直接返回
    return;
  }

  showLoadingToast({
    message: "loading...",
    forbidClick: true,
    duration: 0,
  });

  genCommonRoutes()

  const initData = () => {
    localStorage.removeItem(INSTALLCONFIG_TIME);
    const t = localStorage.getItem(INSTALLCONFIG_TIME + '_tid');
    if (t) {
      // localStorage.removeItem(INSTALLCONFIG_TIME + '_tid');
      clearTimeout(+t);
    }

    return Promise.all(
      [
        useOpenningImage(),
        useActivityList(),
        useGameCategory(),
        useServico(),
        useWallet(),
        useAppPwa(),
      ].map((store) => store.init())
    );
  };

  if (!sealing) {
    ev.on("login", () => {
      initData();
    });

    ev.on("logout", () => {
      initData();
    });
  }

  const initRoutes = async () => {
    const {theme, skin} = useSystemInfo();
    // const {theme, skin} = {theme: 'p4', skin: 'dark_gold'};
    document.body.dataset.theme = theme;
    document.body.dataset.skin = skin;

    const routes = await getRoutes(theme, skin);
    routes.forEach(router.addRoute);
  };

  await Promise.all([initRoutes(), initData(), wait(2)]);
};

const getPwaRedirect = () => {
  return (
    "/" +
    decodeURIComponent(route.query.pwaredirect!.toString()).replace(
      CUSTOM_PROTOCOL + "://",
      ""
    )
  ).replace(/^\/+/, "/");
};

const showPage = async () => {
  // router.removeRoute("openning");
  const isPwaDirect = !!route.query.pwaredirect;
  const url = isPwaDirect ? getPwaRedirect() : location.pathname + location.search;
  await router.replace(url);

  createHeadLinks(useHomeBannersAndTexts().pwaConfig);

  isLoading.value = false;
  closeToast();
};

init()
  .then(showPage)
  .catch((error) => {
    console.error("init error", error);
    showFailToast({
      iconSize: 30,
      message: "Failed to init, please try later",
      duration: 0,
    });

    const { t } = i18n.global
    showDialog({
      title: t('InitDialog.title'),
      message: t('InitDialog.message'),
      showCancelButton: false,
      showConfirmButton: true,
      confirmButtonText: t('InitDialog.confirmButtonText'), 
      className:'initfailed-dialog pointer-events-auto [&_*]:pointer-events-auto',
      theme:'round-button',
      // teleport:'body',
      beforeClose:(action)=>{
        console.log('beforeClose:',action);
        if (action === 'confirm') {
          location.reload();
        }
      },
    })
  });

const cachedRoutes = ref<string[]>([]);
watch(route, (newRoute) => {
  if (newRoute.meta.keepAlive) {
    if (!cachedRoutes.value.includes(newRoute.name as string)) {
      cachedRoutes.value.push(newRoute.name as string);
    }
  }

  if (needUpdate.value && ["home", "promocao", "meu"].includes(newRoute.name as string)) {
    if (needUpdate.value === "popup") {
      showPopup(Reload);
    } else {
      location.reload();
    }
  }
});

watch(
  isLoading,
  (newVal) => {
    if (isPlatform && !newVal) {
      // 在安卓包中且不是马甲包，关闭启动页
      callJSBridge("closeSplashScreen", "");
    }
    // 如果是在iframe中，关闭PWA遮罩loding
    if (isInIframe && newVal) {
      setTimeout(() => {
        window.parent.postMessage("iframe-content-ready", "*");
      }, 800);
    }
  },
  {
    immediate: true,
  }
);
watch(
  () => useUserInfo().isLoggedin,
  (newVal) => {
    if (newVal && isIOSSafari && !isInPWA) {
      window.location.reload();
    }
  }
);

// 检测断网
window.addEventListener("offline", () => {
  handleOffline();
});
window.addEventListener("online", () => {
  handleOnline();
});

const handleOffline = () => {
  hasNetwork.value = false;
  console.log("当前网络已断开");
};

const handleOnline = () => {
  hasNetwork.value = true;
  setTimeout(() => {
    location.reload();
  }, 1000);
  console.log("当前网络已连接");
};

// if ((window as any).jsBridge) {
//   (window as any).jsBridge.postMessage(
//     'setUserInfo',
//     JSON.stringify(userInfo || {})
//   );
// }
const isLoadingPwa = ref(true);
// const sendAuthToIframe = () => {
//   const iframe: any = document.getElementById("gameIframe");
//   if (iframe && iframe.contentWindow) {
//     isLoadingPwa.value = false;
//   }
// };

const handleIframeMessage = (event: MessageEvent) => {
  if (event.data === "iframe-content-ready") {
    isLoadingPwa.value = false;
  }
};

onMounted(() => {
  window.addEventListener("message", handleIframeMessage);
  // 在 App.vue 或特定页面添加
  document.addEventListener(
    "dblclick",
    (e) => {
      e.preventDefault();
    },
    { passive: false }
  );

});

onUnmounted(() => {
  window.removeEventListener("message", handleIframeMessage);
  window.removeEventListener("offline", handleOffline);
  window.removeEventListener("online", handleOnline);
});
</script>

<template>
  <notificationModal />
  <template v-if="iframeUrl">
    <FullScreenLoading v-if="isLoadingPwa" />
    <iframe
      id="gameIframe"
      style="width: 100%; height: calc(var(--vh, 1vh) * 100); border: none"
      :src="iframeUrl"
      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
      allow="screen-wake-lock"
    />
  </template>
  <template v-else>
    <OpenningPage v-if="isLoading && !isTemplate" />
    <router-view v-slot="{ Component }">
      <ErrorBoundary>
        <keep-alive :include="cachedRoutes">
          <component :is="Component" />
        </keep-alive>
      </ErrorBoundary>
    </router-view>
  </template>
</template>
